import { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";

import { Timeline } from "@mui/icons-material";
import { Box, Paper } from "@mui/material";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { useAlarmPaletteResolver } from "../../../../../../hooks/use-alarm-color";
import {
  buildTimePanelChronogramTicks,
  buildTimePanelCoverageSegments,
  buildVisibleTrendSeriesPoints,
  computeTimePanelTrendPlotScale,
  computeTimePanelDataMarkDimensions,
  resolveDataMarkPlotPosition,
  resolveDataMarkTrendVertex,
  type ITimePanelTrendPlotScale,
} from "../../utils/time-panel-chart";
import {
  getTimePanelMeasureKey,
  isTimePanelMeasureHidden,
  isTimePanelMeasureSelected,
} from "../../utils/time-panel-measures";
import type { IChartData } from "../../../scheme/components/analytics/interfaces";
import { lineColors } from "../../../scheme/components/analytics/constants";
import { useRangeFrameDrag } from "../../hooks/use-range-frame-drag";
import type { IMultiAnalysisDataMark, IMultiAnalysisTimeRange } from "../../types";
import { isDateInRange } from "../../utils/data-marks";
import {
  clampFrameRange,
  getFramePlotRatios,
  isFullTimeRange,
  normalizeTimeRange,
  parseMultiAnalysisChartTime,
  timeToRatio,
} from "../../utils/range-frame";
import { MarkTooltip } from "./data-mark-tooltip";
import { DataMarkGroupPicker } from "./data-mark-group-picker";
import {
  groupTimePanelMarks,
  resolveTimePanelMarkGroupAlarmStatus,
  type ITimePanelMarkGroup,
} from "../../utils/time-panel-mark-groups";
import { RangeSelectorFrame } from "./range-selector-frame";

const FALLBACK_CONTAINER_WIDTH = 640;
const FALLBACK_CONTAINER_HEIGHT = 110;
const TIME_LABEL_HEIGHT = 16;
const STATUS_BAR_HEIGHT = 10;
const TIME_PANEL_CHART_MIN_PLOT_HEIGHT = 72;
const DATA_MARK_GROUP_LABEL_HEIGHT = 12;
const TREND_PADDING = {
  top: 6,
  bottom: 6,
};
const PADDING = {
  top: 4,
  right: 12,
  bottom: 4,
  left: 8,
};

export const TIME_PANEL_CHART_MIN_HEIGHT =
  PADDING.top + PADDING.bottom + TIME_LABEL_HEIGHT + TIME_PANEL_CHART_MIN_PLOT_HEIGHT;

const INACTIVE_MARK_FILL = "#c4cad6";
const INACTIVE_MARK_STROKE = "#a8b0bf";
const DIM_OVERLAY_FILL = "rgba(120, 136, 168, 0.28)";
const DIMMED_SERIES_OPACITY = 0.2;
const EMPTY_SELECTED_MEASURE_KEYS: ReadonlySet<string> = new Set();
const EMPTY_HIDDEN_MEASURE_KEYS: ReadonlySet<string> = new Set();
const TIME_AXIS_LINE = "rgba(148, 160, 182, 0.7)";
const TIME_GRID_LINE = "rgba(148, 160, 182, 0.42)";
const TIME_LABEL_COLOR = "#8b95a8";
const PLOT_BACKGROUND = "#f7f9fc";
const DATA_MARK_TOOLTIP_OFFSET_X = 10;
const DATA_MARK_TOOLTIP_OFFSET_Y = 6;
const DATA_MARK_TOOLTIP_ESTIMATED_HEIGHT = 118;
const DATA_MARK_TOOLTIP_ESTIMATED_WIDTH = 300;
const VIEWPORT_EDGE_PADDING = 8;

const computeDataMarkTooltipPosition = (
  clientX: number,
  clientY: number,
): { left: number; top: number; transform: string } => {
  const maxLeft = window.innerWidth - DATA_MARK_TOOLTIP_ESTIMATED_WIDTH - VIEWPORT_EDGE_PADDING;
  const maxTop = window.innerHeight - DATA_MARK_TOOLTIP_ESTIMATED_HEIGHT - VIEWPORT_EDGE_PADDING;

  let left = clientX + DATA_MARK_TOOLTIP_OFFSET_X;
  let top = clientY - DATA_MARK_TOOLTIP_OFFSET_Y;
  let transform = "translateY(-100%)";

  if (top - DATA_MARK_TOOLTIP_ESTIMATED_HEIGHT < VIEWPORT_EDGE_PADDING) {
    top = clientY + DATA_MARK_TOOLTIP_OFFSET_Y;
    transform = "none";
  }

  if (top > maxTop) {
    top = maxTop;
    transform = "none";
  }

  if (left > maxLeft) {
    left = clientX - DATA_MARK_TOOLTIP_ESTIMATED_WIDTH - DATA_MARK_TOOLTIP_OFFSET_X;
  }

  return {
    left: Math.max(VIEWPORT_EDGE_PADDING, Math.min(left, maxLeft)),
    top: Math.max(VIEWPORT_EDGE_PADDING, top),
    transform,
  };
};

interface ITimePanelChartProps {
  globalRange: IMultiAnalysisTimeRange;
  frameRange: IMultiAnalysisTimeRange;
  trendSeries: IChartData[];
  dataMarks: IMultiAnalysisDataMark[];
  selectedMarkKeys: ReadonlySet<string>;
  selectedMeasureKeys?: ReadonlySet<string>;
  hiddenMeasureKeys?: ReadonlySet<string>;
  groupOverlappingMarks?: boolean;
  dataMarkPlacement?: "trend" | "top";
  dataMarkSelectionHintId?: string;
  hasSelectedPoints: boolean;
  onFrameRangeChange: (range: IMultiAnalysisTimeRange) => void;
  onFrameRangeReset: () => void;
  onMarkClick: (mark: IMultiAnalysisDataMark, event: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) => void;
  renderMarkTooltip?: (mark: IMultiAnalysisDataMark) => ReactNode | null;
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const collectTrendValuesInGlobalRange = (series: IChartData[], globalRange: IMultiAnalysisTimeRange): number[] => {
  const globalBounds = normalizeTimeRange(globalRange);

  if (!globalBounds) {
    return [];
  }

  return series.flatMap(item =>
    item.items.flatMap(({ date, value }) => {
      const timestamp = parseMultiAnalysisChartTime(date);

      if (!Number.isFinite(timestamp) || timestamp < globalBounds.start || timestamp > globalBounds.end) {
        return [];
      }

      const numeric = Number(value);

      return Number.isFinite(numeric) ? [numeric] : [];
    }),
  );
};

const valueToPlotY = (
  value: number,
  scale: ITimePanelTrendPlotScale,
  plotTop: number,
  trendHeight: number,
  paddingTop: number,
): number => {
  const normalized = scale.minValue === scale.maxValue ? 0.5 : (value - scale.minValue) / scale.valueSpan;
  const drawableTrendHeight = Math.max(1, trendHeight - paddingTop - TREND_PADDING.bottom);

  return plotTop + paddingTop + (1 - normalized) * drawableTrendHeight;
};

const buildNormalizedSeries = (
  series: IChartData[],
  plotWidth: number,
  trendHeight: number,
  plotTop: number,
  globalRange: IMultiAnalysisTimeRange,
  scale: ITimePanelTrendPlotScale,
  paddingTop: number,
): Array<{
  color: string;
  idPoint?: number;
  measureKey: string | null;
  points: Array<{ x: number; y: number; date: string; value: number }>;
}> => {
  const globalBounds = normalizeTimeRange(globalRange);

  return series.map((item, index) => {
    const points = item.items
      .map(({ date, value }) => {
        const timestamp = parseMultiAnalysisChartTime(date);

        if (
          !Number.isFinite(timestamp) ||
          !globalBounds ||
          timestamp < globalBounds.start ||
          timestamp > globalBounds.end
        ) {
          return null;
        }

        const numeric = Number(value);

        if (!Number.isFinite(numeric)) {
          return null;
        }

        const ratio = clamp(timeToRatio(timestamp, globalRange), 0, 1);

        return {
          date,
          value: numeric,
          x: PADDING.left + ratio * plotWidth,
          y: valueToPlotY(numeric, scale, plotTop, trendHeight, paddingTop),
        };
      })
      .filter((point): point is { x: number; y: number; date: string; value: number } => point !== null);

    return {
      color: item.color ?? lineColors[index % lineColors.length],
      idPoint: item.idPoint,
      measureKey: getTimePanelMeasureKey(item.idPoint, item.parameterName, item.name, item.idMeasure),
      points,
    };
  });
};

export const TimePanelChart: FC<ITimePanelChartProps> = ({
  globalRange,
  frameRange,
  trendSeries,
  dataMarks,
  selectedMarkKeys,
  selectedMeasureKeys = EMPTY_SELECTED_MEASURE_KEYS,
  hiddenMeasureKeys = EMPTY_HIDDEN_MEASURE_KEYS,
  groupOverlappingMarks = false,
  dataMarkPlacement = "trend",
  dataMarkSelectionHintId,
  hasSelectedPoints,
  onFrameRangeChange,
  onFrameRangeReset,
  onMarkClick,
  renderMarkTooltip,
}) => {
  const { formatMessage } = useIntl();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(FALLBACK_CONTAINER_WIDTH);
  const [containerHeight, setContainerHeight] = useState(FALLBACK_CONTAINER_HEIGHT);
  const [hoveredMark, setHoveredMark] = useState<IMultiAnalysisDataMark | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [activeMarkGroup, setActiveMarkGroup] = useState<{
    marks: IMultiAnalysisDataMark[];
    anchorPosition: { left: number; top: number };
  } | null>(null);

  useEffect(() => {
    setActiveMarkGroup(null);
    setHoveredMark(null);
    setHoverPosition(null);
  }, [dataMarks, frameRange, containerWidth, containerHeight]);

  const layoutWidth = containerWidth > 0 ? containerWidth : FALLBACK_CONTAINER_WIDTH;
  const svgHeight = containerHeight > 0 ? containerHeight : FALLBACK_CONTAINER_HEIGHT;
  const plotWidth = Math.max(120, layoutWidth - PADDING.left - PADDING.right);
  const plotTop = PADDING.top + TIME_LABEL_HEIGHT;
  const plotHeight = Math.max(
    TIME_PANEL_CHART_MIN_PLOT_HEIGHT,
    svgHeight - PADDING.top - PADDING.bottom - TIME_LABEL_HEIGHT,
  );
  const trendHeight = Math.max(40, plotHeight - STATUS_BAR_HEIGHT);
  const statusBarTop = plotTop + trendHeight;
  const dataMarkDimensions = useMemo(() => computeTimePanelDataMarkDimensions(trendHeight), [trendHeight]);
  const trendPaddingTop =
    dataMarkDimensions.size + TREND_PADDING.top + (dataMarkPlacement === "top" ? DATA_MARK_GROUP_LABEL_HEIGHT : 0);

  const handleFrameRangeChange = useCallback(
    (range: IMultiAnalysisTimeRange) => {
      onFrameRangeChange(clampFrameRange(range, globalRange));
    },
    [globalRange, onFrameRangeChange],
  );

  const trendPlotScale = useMemo(
    () => computeTimePanelTrendPlotScale(collectTrendValuesInGlobalRange(trendSeries, globalRange)),
    [globalRange, trendSeries],
  );

  const normalizedSeries = useMemo(
    () =>
      buildNormalizedSeries(trendSeries, plotWidth, trendHeight, plotTop, globalRange, trendPlotScale, trendPaddingTop),
    [globalRange, plotTop, plotWidth, trendHeight, trendPaddingTop, trendPlotScale, trendSeries],
  );

  const chronogramTicks = useMemo(() => {
    if (!globalRange.start || !globalRange.end) {
      return [];
    }

    return buildTimePanelChronogramTicks(globalRange);
  }, [globalRange.end, globalRange.start]);

  const frameRect = useMemo(() => {
    const { leftRatio, rightRatio } = getFramePlotRatios(frameRange, globalRange);

    return {
      x: PADDING.left + leftRatio * plotWidth,
      width: Math.max(8, (rightRatio - leftRatio) * plotWidth),
    };
  }, [frameRange, globalRange, plotWidth]);

  const coverageSegments = useMemo(
    () => buildTimePanelCoverageSegments(trendSeries, globalRange),
    [globalRange, trendSeries],
  );

  const { handlePointerDown, handlePointerMove, handlePointerUp } = useRangeFrameDrag({
    globalRange,
    frameRange,
    onFrameRangeChange: handleFrameRangeChange,
    plotLeft: PADDING.left,
    plotWidth,
  });

  useLayoutEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return undefined;
    }

    const updateSize = () => {
      const nextWidth = element.clientWidth;
      const nextHeight = element.clientHeight;

      if (nextWidth > 0) {
        setContainerWidth(nextWidth);
      }

      if (nextHeight > 0) {
        setContainerHeight(nextHeight);
      }
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  });

  const getContainerLeft = (): number => containerRef.current?.getBoundingClientRect().left ?? 0;

  const handleContainerPointerMove = (event: React.PointerEvent) => {
    handlePointerMove(event, getContainerLeft());
  };

  const markPositions = useMemo(() => {
    const { size: markSize, offset: markOffset } = dataMarkDimensions;
    const trendVertices =
      dataMarkPlacement === "trend" ? buildVisibleTrendSeriesPoints(normalizedSeries, trendSeries) : [];

    return dataMarks.flatMap(mark => {
      const vertex = resolveDataMarkTrendVertex(mark, trendVertices);
      const { x, y } = resolveDataMarkPlotPosition(
        mark,
        globalRange,
        PADDING.left,
        plotWidth,
        plotTop,
        trendHeight,
        vertex,
      );

      const markTipY =
        dataMarkPlacement === "top"
          ? plotTop + DATA_MARK_GROUP_LABEL_HEIGHT + markSize
          : clamp(y - markOffset, plotTop + trendPaddingTop, plotTop + trendHeight - TREND_PADDING.bottom - 1);

      return [
        {
          mark,
          measureKey:
            vertex?.measureKey ?? getTimePanelMeasureKey(mark.idPoint, mark.measureName, undefined, mark.idMeasure),
          x,
          tipY: markTipY,
          baseY: markTipY - markSize,
        },
      ];
    });
  }, [
    dataMarkDimensions,
    dataMarkPlacement,
    dataMarks,
    globalRange,
    normalizedSeries,
    plotTop,
    plotWidth,
    trendHeight,
    trendPaddingTop,
    trendSeries,
  ]);

  const selectedMarkPositions = useMemo(
    () =>
      markPositions.filter(
        ({ mark, measureKey }) =>
          selectedMarkKeys.has(mark.key) &&
          isDateInRange(mark.date, frameRange) &&
          !isTimePanelMeasureHidden(measureKey, hiddenMeasureKeys),
      ),
    [frameRange, hiddenMeasureKeys, markPositions, selectedMarkKeys],
  );

  const markGroups = useMemo<ITimePanelMarkGroup[]>(() => {
    const visible = markPositions.filter(({ measureKey }) => !isTimePanelMeasureHidden(measureKey, hiddenMeasureKeys));

    if (!groupOverlappingMarks) {
      return visible
        .sort((left, right) => {
          const leftSelected = isTimePanelMeasureSelected(left.measureKey, selectedMeasureKeys) ? 1 : 0;
          const rightSelected = isTimePanelMeasureSelected(right.measureKey, selectedMeasureKeys) ? 1 : 0;
          return leftSelected - rightSelected;
        })
        .map(position => ({ ...position, positions: [position] }));
    }

    const spacing = dataMarkDimensions.size + 4;
    return [
      ...groupTimePanelMarks(
        visible.filter(({ mark }) => !isDateInRange(mark.date, frameRange)),
        spacing,
      ),
      ...groupTimePanelMarks(
        visible.filter(({ mark }) => isDateInRange(mark.date, frameRange)),
        spacing,
      ),
    ];
  }, [
    dataMarkDimensions.size,
    frameRange,
    groupOverlappingMarks,
    hiddenMeasureKeys,
    markPositions,
    selectedMeasureKeys,
  ]);

  const tooltipPosition = useMemo(
    () => (hoverPosition ? computeDataMarkTooltipPosition(hoverPosition.x, hoverPosition.y) : null),
    [hoverPosition],
  );

  const hasTrendData = normalizedSeries.some(series => series.points.length > 0);
  const showChart = hasSelectedPoints && (hasTrendData || dataMarks.length > 0);
  const showDimOverlays = !isFullTimeRange(frameRange, globalRange);
  const leftDimWidth = Math.max(0, frameRect.x - PADDING.left);
  const rightDimX = frameRect.x + frameRect.width;
  const rightDimWidth = Math.max(0, PADDING.left + plotWidth - rightDimX);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        userSelect: "none",
      }}
      onPointerMove={showChart ? handleContainerPointerMove : undefined}
      onPointerUp={showChart ? handlePointerUp : undefined}
      onPointerLeave={showChart ? handlePointerUp : undefined}
    >
      {!hasSelectedPoints ? (
        <EmptyPlaceholder
          icon={<Timeline />}
          text={formatMessage({ id: "pages.multiAnalysis.chartPlaceholder" })}
          wrapperProps={{ sx: { height: "100%", minHeight: 0 } }}
        />
      ) : !hasTrendData && dataMarks.length === 0 ? (
        <EmptyPlaceholder
          icon={<Timeline />}
          text={formatMessage({ id: "pages.multiAnalysis.chartEmpty" })}
          wrapperProps={{ sx: { height: "100%", minHeight: 0 } }}
        />
      ) : (
        <>
          <svg
            aria-label={formatMessage({ id: "pages.multiAnalysis.timePanel.title" })}
            height="100%"
            role="img"
            width="100%"
            viewBox={`0 0 ${layoutWidth} ${svgHeight}`}
            preserveAspectRatio="none"
            style={{ display: "block" }}
          >
            <rect x={PADDING.left} y={plotTop} width={plotWidth} height={plotHeight} fill={PLOT_BACKGROUND} />
            {chronogramTicks.map(tick => (
              <text
                key={`label-${tick.key}`}
                x={PADDING.left + (tick.left / 100) * plotWidth}
                y={PADDING.top + 11}
                fill={TIME_LABEL_COLOR}
                fontSize={10}
                textAnchor="middle"
                pointerEvents="none"
              >
                {tick.label}
              </text>
            ))}
            <rect
              x={frameRect.x}
              y={plotTop}
              width={frameRect.width}
              height={plotHeight}
              fill="rgba(255, 255, 255, 0.94)"
              pointerEvents="none"
            />
            {normalizedSeries
              .map((series, index) => ({ series, index }))
              .sort((left, right) => {
                const leftSelected = isTimePanelMeasureSelected(left.series.measureKey, selectedMeasureKeys) ? 1 : 0;
                const rightSelected = isTimePanelMeasureSelected(right.series.measureKey, selectedMeasureKeys) ? 1 : 0;

                return leftSelected - rightSelected;
              })
              .map(({ series, index }) => {
                if (series.points.length < 2 || isTimePanelMeasureHidden(series.measureKey, hiddenMeasureKeys)) {
                  return null;
                }

                const isHighlighted = isTimePanelMeasureSelected(series.measureKey, selectedMeasureKeys);
                const isDimmed = selectedMeasureKeys.size > 0 && !isHighlighted;
                const polyline = series.points.map(point => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" ");

                return (
                  <polyline
                    key={`trend-${index}`}
                    fill="none"
                    opacity={isDimmed ? DIMMED_SERIES_OPACITY : 1}
                    points={polyline}
                    stroke={series.color}
                    strokeWidth={isHighlighted && selectedMeasureKeys.size > 0 ? 2 : 1}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            {coverageSegments.map((segment, index) => {
              const x = PADDING.left + segment.leftRatio * plotWidth;
              const width = segment.widthRatio * plotWidth;

              return (
                <g key={segment.key} pointerEvents="none">
                  {index > 0 ? (
                    <line
                      x1={x}
                      x2={x}
                      y1={statusBarTop}
                      y2={statusBarTop + STATUS_BAR_HEIGHT}
                      stroke={TIME_GRID_LINE}
                      strokeWidth={1}
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : null}
                  <rect
                    x={x}
                    y={statusBarTop}
                    width={width}
                    height={STATUS_BAR_HEIGHT}
                    fill={segment.hasData ? "#b8c5d9" : "#e6ebf3"}
                    fillOpacity={0.9}
                    stroke="none"
                  />
                </g>
              );
            })}
            {showDimOverlays && leftDimWidth > 0 ? (
              <rect
                x={PADDING.left}
                y={plotTop}
                width={leftDimWidth}
                height={plotHeight}
                fill={DIM_OVERLAY_FILL}
                pointerEvents="none"
              />
            ) : null}
            {showDimOverlays && rightDimWidth > 0 ? (
              <rect
                x={rightDimX}
                y={plotTop}
                width={rightDimWidth}
                height={plotHeight}
                fill={DIM_OVERLAY_FILL}
                pointerEvents="none"
              />
            ) : null}
            <g pointerEvents="none">
              <line
                x1={PADDING.left}
                x2={PADDING.left + plotWidth}
                y1={plotTop}
                y2={plotTop}
                stroke={TIME_AXIS_LINE}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1={PADDING.left}
                x2={PADDING.left + plotWidth}
                y1={statusBarTop}
                y2={statusBarTop}
                stroke={TIME_AXIS_LINE}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1={PADDING.left}
                x2={PADDING.left}
                y1={plotTop}
                y2={plotTop + plotHeight}
                stroke={TIME_GRID_LINE}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1={PADDING.left + plotWidth}
                x2={PADDING.left + plotWidth}
                y1={plotTop}
                y2={plotTop + plotHeight}
                stroke={TIME_GRID_LINE}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              {chronogramTicks.map(tick => {
                const x = PADDING.left + (tick.left / 100) * plotWidth;

                return (
                  <line
                    key={`grid-${tick.key}`}
                    x1={x}
                    x2={x}
                    y1={plotTop}
                    y2={plotTop + plotHeight}
                    stroke={TIME_GRID_LINE}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            </g>
            {selectedMarkPositions.map(({ mark, x }) => (
              <line
                key={`selected-mark-line-${mark.key}`}
                x1={x}
                x2={x}
                y1={plotTop}
                y2={plotTop + plotHeight}
                stroke="#173a8a"
                strokeDasharray="3 3"
                strokeWidth={1}
                pointerEvents="none"
              />
            ))}
            {markGroups.map(({ positions, x, tipY, baseY }) => {
              const { mark, measureKey } = positions[0]!;
              const isGroup = positions.length > 1;
              const isInFrame = isDateInRange(mark.date, frameRange);
              const isMeasureHighlighted = isTimePanelMeasureSelected(measureKey, selectedMeasureKeys);
              const isDimmed = selectedMeasureKeys.size > 0 && !isMeasureHighlighted;
              const isInteractive = isInFrame && !isDimmed;
              const isSelected = isInteractive && positions.some(position => selectedMarkKeys.has(position.mark.key));
              const isHovered = isInteractive && hoveredMark?.key === mark.key;
              const alarmStatus = resolveTimePanelMarkGroupAlarmStatus(positions);
              const color = isInFrame ? resolveAlarmColor(alarmStatus) : INACTIVE_MARK_FILL;
              const { size: markSize, strokeWidth, selectedStrokeWidth } = dataMarkDimensions;
              const halfMarkSize = markSize / 2;

              return (
                <g
                  key={mark.key}
                  role={groupOverlappingMarks && isInteractive ? "button" : undefined}
                  tabIndex={groupOverlappingMarks && isInteractive ? 0 : undefined}
                  aria-label={
                    groupOverlappingMarks
                      ? formatMessage({ id: "pages.multiAnalysis.timePanel.dataMarks" }, { count: positions.length })
                      : undefined
                  }
                  style={{
                    cursor: isInteractive ? "pointer" : "default",
                    opacity: isDimmed ? DIMMED_SERIES_OPACITY : isInFrame ? 1 : 0.85,
                    pointerEvents: isInteractive ? "auto" : "none",
                  }}
                  onClick={
                    isInteractive
                      ? event => {
                          if (isGroup) {
                            setHoveredMark(null);
                            setHoverPosition(null);
                            setActiveMarkGroup({
                              marks: positions.map(position => position.mark),
                              anchorPosition: { left: event.clientX, top: event.clientY },
                            });
                            return;
                          }
                          onMarkClick(mark, {
                            shiftKey: event.shiftKey,
                            ctrlKey: event.ctrlKey,
                            metaKey: event.metaKey,
                          });
                        }
                      : undefined
                  }
                  onKeyDown={
                    groupOverlappingMarks && isInteractive
                      ? event => {
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          if (isGroup) {
                            const bounds = event.currentTarget.getBoundingClientRect();
                            setActiveMarkGroup({
                              marks: positions.map(position => position.mark),
                              anchorPosition: { left: bounds.left, top: bounds.bottom },
                            });
                          } else {
                            onMarkClick(mark, event);
                          }
                        }
                      : undefined
                  }
                  onMouseEnter={
                    isInteractive && !isGroup
                      ? event => {
                          setHoveredMark(mark);
                          setHoverPosition({ x: event.clientX, y: event.clientY });
                        }
                      : undefined
                  }
                  onMouseMove={
                    isInteractive ? event => setHoverPosition({ x: event.clientX, y: event.clientY }) : undefined
                  }
                  onMouseLeave={
                    isInteractive
                      ? () => {
                          setHoveredMark(null);
                          setHoverPosition(null);
                        }
                      : undefined
                  }
                >
                  {isGroup ? (
                    <rect
                      x={x - halfMarkSize - 2}
                      y={baseY - DATA_MARK_GROUP_LABEL_HEIGHT}
                      width={markSize + 4}
                      height={markSize + DATA_MARK_GROUP_LABEL_HEIGHT}
                      fill="transparent"
                    />
                  ) : null}
                  <polygon
                    points={`${x},${tipY} ${x - halfMarkSize},${baseY} ${x + halfMarkSize},${baseY}`}
                    fill={color}
                    stroke={isSelected || isHovered ? "#173a8a" : isInFrame ? "#8b95a8" : INACTIVE_MARK_STROKE}
                    strokeWidth={isSelected || isHovered ? selectedStrokeWidth : strokeWidth}
                  />
                  {isGroup ? (
                    <text x={x} y={baseY - 2} textAnchor="middle" fontSize={10} fill="#173a8a" pointerEvents="none">
                      +
                    </text>
                  ) : null}
                </g>
              );
            })}
            <RangeSelectorFrame
              globalRange={globalRange}
              frameRange={frameRange}
              plotLeft={PADDING.left}
              plotTop={plotTop}
              plotWidth={plotWidth}
              plotHeight={plotHeight}
              onPointerDownMove={event => handlePointerDown("move", event, getContainerLeft())}
              onPointerDownResizeLeft={event => handlePointerDown("resize-left", event, getContainerLeft())}
              onPointerDownResizeRight={event => handlePointerDown("resize-right", event, getContainerLeft())}
              onDoubleClickReset={onFrameRangeReset}
            />
          </svg>
          {activeMarkGroup ? (
            <DataMarkGroupPicker
              marks={activeMarkGroup.marks}
              anchorPosition={activeMarkGroup.anchorPosition}
              selectedMarkKeys={selectedMarkKeys}
              selectionHintId={dataMarkSelectionHintId}
              onMarkClick={onMarkClick}
              onClose={() => setActiveMarkGroup(null)}
              renderMarkTooltip={renderMarkTooltip}
            />
          ) : null}
          {hoveredMark && tooltipPosition
            ? createPortal(
                <Paper
                  elevation={4}
                  sx={{
                    position: "fixed",
                    left: tooltipPosition.left,
                    top: tooltipPosition.top,
                    transform: tooltipPosition.transform,
                    zIndex: 1400,
                    pointerEvents: "none",
                    bgcolor: "#ffffff",
                  }}
                >
                  <MarkTooltip mark={hoveredMark} renderMarkTooltip={renderMarkTooltip} />
                </Paper>,
                document.body,
              )
            : null}
        </>
      )}
    </Box>
  );
};
